import {
    Button, Form, Input, Modal,
} from 'antd';
import { useTranslation } from 'react-i18next';
import {
    forwardRef, useEffect, useImperativeHandle, useRef, useState,
} from 'react';
import { useForm } from 'antd/lib/form/Form';
import FileDownload from 'js-file-download';
import { DownloadOutlined } from '@ant-design/icons';
import { getRequiredFieldMessage } from '../../helpers/form';
import { useFetch } from '../../hooks/useFetch';
import { IOrder } from '../../types/order';
import FetchService from '../../services/fetch';

interface IRusPostManifestFormModalProps {
    onHide?: () => void;
}

export interface IRusPostManifestFormModalRef {
    hide: () => void;
    show: (orders?: IOrder[]) => void;
}

const RusPostManifestFormModal = forwardRef(({ onHide }: IRusPostManifestFormModalProps, ref) => {
    const fetch = useFetch();
    const [form] = useForm();
    const [downloading, setDownloading] = useState(false);
    const [open, setOpen] = useState(false);
    const { t } = useTranslation();
    const selectedRows = useRef<IOrder[]>([]);
    const opened = useRef(false);

    useEffect(() => {
        if (open) {
            form.setFieldValue('email', 'manifest@logistics-solution.eu');
            opened.current = true;
        } else if (typeof onHide === 'function' && opened.current) {
            onHide();
        }
    }, [open]);

    const hide = () => {
        selectedRows.current = [];
        setOpen(false);
    };

    const show = (orders: IOrder[] = []) => {
        selectedRows.current = orders;
        setOpen(true);
    };

    useImperativeHandle(ref, () => ({
        hide,
        show,
    }));

    const onDownloadClick = async () => {
        setDownloading(true);
        const result = await FetchService.downloadFile({
            url: '/ruspost/manifest',
            method: 'POST',
            data: {
                orders: selectedRows.current.length > 0 ? selectedRows.current.map((el) => el.id) : undefined,
            },
        });
        if (result.success && result.data) {
            FileDownload(result.data, `${t('manifest')}.xlsx`, 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet');
        }
        setDownloading(false);
    };

    const onSendClick = async () => {
        const fields = await form.validateFields();
        const response = await fetch.request({
            url: '/ruspost/manifest/send',
            data: {
                ...fields,
                orders: selectedRows.current.length > 0 ? selectedRows.current.map((el) => el.id) : undefined,
            },
            method: 'post',
        });
        setOpen(!response.success);
    };

    return (
        <Modal
            title={t('confirmAction')}
            open={open}
            onCancel={hide}
            onOk={onSendClick}
            okButtonProps={{ loading: fetch.loading }}
            okText={t('sendManifest')}
        >
            <Form form={form} layout="vertical">
                <Form.Item
                    required
                    label={t('email')}
                    name="email"
                    rules={[{ required: true, message: getRequiredFieldMessage(t('email')) }]}
                >
                    <Input type="email" />
                </Form.Item>
                <Form.Item>
                    <Button
                        loading={downloading}
                        type="primary"
                        icon={<DownloadOutlined />}
                        onClick={onDownloadClick}
                    >
                        {t('manifest')}
                    </Button>
                </Form.Item>
            </Form>
        </Modal>
    );
});

export default RusPostManifestFormModal;
